import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { WebEvent } from '../types/events';
import type {
  CreateWebInviteInput,
  CreatedWebInvite,
  WebEventMember,
  WebInviteMethod,
  WebJoinRequest,
  WebMemberRole,
  WebPeopleSnapshot,
} from '../types/people';
import {
  canChangeEventRoles,
  canManageEventPeople,
  changeEventMemberRole,
  createGuestInvite,
  formatInviteCode,
  loadPeopleSnapshot,
  removeEventMember,
  reviewJoinRequest,
  subscribeToPeopleChanges,
} from '../services/people';
import './people.css';

type PeoplePanelProps = {
  event: WebEvent;
  currentUserId: string;
};

function personName(member: WebEventMember): string {
  return member.profile?.full_name?.trim() || member.profile?.email?.trim() || 'Gathrd guest';
}

function requestName(request: WebJoinRequest): string {
  return request.profile?.full_name?.trim() || request.profile?.email?.trim() || 'Gathrd guest';
}

function formatPeopleDate(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

async function deliverInvite(result: CreatedWebInvite, input: CreateWebInviteInput, eventName: string) {
  const recipient = String(input.recipient ?? '').trim();
  if (input.method === 'email' && recipient) {
    const subject = encodeURIComponent(`You're invited to ${eventName} on Gathrd`);
    const body = encodeURIComponent(result.message);
    window.location.href = `mailto:${recipient}?subject=${subject}&body=${body}`;
    return;
  }
  if (input.method === 'sms' && recipient) {
    window.location.href = `sms:${recipient}?body=${encodeURIComponent(result.message)}`;
    return;
  }
  if (navigator.share) {
    await navigator.share({ title: `Gathrd invite · ${eventName}`, text: result.message });
    return;
  }
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(result.message);
  }
}

export function PeoplePanel({ event, currentUserId }: PeoplePanelProps) {
  const [snapshot, setSnapshot] = useState<WebPeopleSnapshot>({
    members: [],
    invites: [],
    requests: [],
    permissions: null,
  });
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteMethod, setInviteMethod] = useState<WebInviteMethod>('email');
  const [inviteRole, setInviteRole] = useState<WebMemberRole>('member');
  const [inviteRecipient, setInviteRecipient] = useState('');
  const [inviteName, setInviteName] = useState('');

  const canManage = canManageEventPeople(snapshot.permissions);
  const canChangeRoles = canChangeEventRoles(snapshot.permissions);
  const ownerId = event.organizer_id;

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setSnapshot(await loadPeopleSnapshot(event.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load event people.');
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => {
    setLoading(true);
    void refresh();
    return subscribeToPeopleChanges(event.id, () => {
      void refresh();
    });
  }, [event.id, refresh]);

  const pendingInvites = useMemo(
    () => snapshot.invites.filter((invite) => !['accepted', 'expired', 'revoked'].includes(String(invite.status ?? '').toLowerCase())),
    [snapshot.invites],
  );

  async function handleInviteSubmit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (!canManage || busyKey) return;
    const input: CreateWebInviteInput = {
      method: inviteMethod,
      role: inviteRole,
      recipient: inviteRecipient,
      fullName: inviteName,
    };

    setBusyKey('invite');
    setError(null);
    setNotice(null);
    try {
      const result = await createGuestInvite(event, currentUserId, input);
      setNotice(`Invite ${formatInviteCode(result.invite.invite_code ?? '')} created with ${inviteRole === 'co_admin' ? 'co-admin' : 'guest'} rights.`);
      setInviteName('');
      setInviteRecipient('');
      setInviteRole('member');
      setShowInvite(false);
      await refresh();
      try {
        await deliverInvite(result, input, event.name);
      } catch {
        if (navigator.clipboard) await navigator.clipboard.writeText(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create this invite.');
    } finally {
      setBusyKey(null);
    }
  }

  async function handleRoleChange(member: WebEventMember) {
    const nextRole: WebMemberRole = member.role === 'co_admin' ? 'member' : 'co_admin';
    if (!canChangeRoles || busyKey) return;
    if (!window.confirm(`Change ${personName(member)} to ${nextRole === 'co_admin' ? 'Co-admin' : 'Guest'}?`)) return;
    setBusyKey(`role:${member.user_id}`);
    setError(null);
    try {
      await changeEventMemberRole(event.id, member, nextRole);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to change this role.');
    } finally {
      setBusyKey(null);
    }
  }

  async function handleRemove(member: WebEventMember) {
    if (!canManage || busyKey || member.user_id === ownerId) return;
    if (!window.confirm(`Remove ${personName(member)} from this event?`)) return;
    setBusyKey(`remove:${member.user_id}`);
    setError(null);
    try {
      await removeEventMember(event.id, member.user_id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to remove this person.');
    } finally {
      setBusyKey(null);
    }
  }

  async function handleRequest(request: WebJoinRequest, approve: boolean) {
    if (!canManage || busyKey) return;
    setBusyKey(`request:${request.id}`);
    setError(null);
    try {
      await reviewJoinRequest({
        eventId: event.id,
        eventName: event.name,
        request,
        approve,
        actorUserId: currentUserId,
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to review this request.');
    } finally {
      setBusyKey(null);
    }
  }

  if (loading) {
    return <section className="people-panel"><div className="people-empty">Loading people…</div></section>;
  }

  return (
    <section className="people-panel" aria-live="polite">
      {error && <p className="error-banner people-error">{error}</p>}
      {notice && <p className="people-notice">{notice}</p>}

      <div className="people-summary-grid">
        <article><strong>{snapshot.members.length}</strong><span>Current people</span></article>
        <article><strong>{snapshot.requests.length}</strong><span>Pending requests</span></article>
        <article><strong>{pendingInvites.length}</strong><span>Pending invites</span></article>
      </div>

      <div className="people-toolbar">
        <div>
          <h2>People & roles</h2>
          <p>Synced from the same membership, invite, request, and permission records as mobile.</p>
        </div>
        <div className="people-toolbar-actions">
          <button type="button" className="secondary-button" onClick={() => void refresh()} disabled={Boolean(busyKey)}>Refresh</button>
          {canManage && (
            <button type="button" className="primary-button" onClick={() => setShowInvite((value) => !value)}>
              {showInvite ? 'Close invite' : '+ Invite person'}
            </button>
          )}
        </div>
      </div>

      {showInvite && canManage && (
        <form className="invite-form" onSubmit={handleInviteSubmit}>
          <div className="invite-form-grid">
            <label>
              Rights
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as WebMemberRole)}>
                <option value="member">Guest</option>
                <option value="co_admin">Co-admin</option>
              </select>
            </label>
            <label>
              Delivery
              <select value={inviteMethod} onChange={(e) => setInviteMethod(e.target.value as WebInviteMethod)}>
                <option value="email">Email</option>
                <option value="sms">Text message</option>
                <option value="share">Share / copy</option>
              </select>
            </label>
            <label>
              Name
              <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Guest name" />
            </label>
            {inviteMethod !== 'share' && (
              <label>
                {inviteMethod === 'email' ? 'Email address' : 'Phone number'}
                <input
                  type={inviteMethod === 'email' ? 'email' : 'tel'}
                  value={inviteRecipient}
                  onChange={(e) => setInviteRecipient(e.target.value)}
                  required
                />
              </label>
            )}
          </div>
          <div className="invite-form-footer">
            <span>{inviteRole === 'co_admin' ? 'Co-admins can invite guests, moderate photos, remove members, and help manage the event.' : 'Guests can join, upload, and view the event gallery.'}</span>
            <button className="primary-button" type="submit" disabled={busyKey === 'invite'}>
              {busyKey === 'invite' ? 'Creating…' : 'Create invite'}
            </button>
          </div>
        </form>
      )}

      {canManage && snapshot.requests.length > 0 && (
        <div className="people-section">
          <div className="people-section-heading">
            <h3>Pending join requests</h3>
            <span>Needs review</span>
          </div>
          <div className="people-list">
            {snapshot.requests.map((request) => (
              <article className="person-row request-row" key={request.id}>
                <div className="person-avatar">{requestName(request).charAt(0).toUpperCase()}</div>
                <div className="person-copy">
                  <strong>{requestName(request)}</strong>
                  <span>{request.profile?.email || 'No email'} · Requested {formatPeopleDate(request.created_at)}</span>
                </div>
                <div className="person-actions">
                  <button className="primary-button small-action" type="button" onClick={() => void handleRequest(request, true)} disabled={Boolean(busyKey)}>Approve</button>
                  <button className="secondary-button small-action" type="button" onClick={() => void handleRequest(request, false)} disabled={Boolean(busyKey)}>Deny</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      <div className="people-section">
        <div className="people-section-heading"><h3>Current people</h3></div>
        <div className="people-list">
          {snapshot.members.length === 0 ? (
            <div className="people-empty">No event members are visible to this account.</div>
          ) : snapshot.members.map((member) => {
            const isOwner = member.user_id === ownerId;
            const isCurrentUser = member.user_id === currentUserId;
            return (
              <article className="person-row" key={member.user_id}>
                <div className="person-avatar">{personName(member).charAt(0).toUpperCase()}</div>
                <div className="person-copy">
                  <strong>{personName(member)} {isOwner ? '👑' : member.role === 'co_admin' ? '🛡️' : ''}</strong>
                  <span>{isOwner ? 'Owner' : member.role === 'co_admin' ? 'Co-admin' : 'Guest'}{isCurrentUser ? ' · You' : ''}{member.joined_at ? ` · Joined ${formatPeopleDate(member.joined_at)}` : ''}</span>
                </div>
                {!isOwner && canManage && (
                  <div className="person-actions">
                    {canChangeRoles && (
                      <button className="secondary-button small-action" type="button" onClick={() => void handleRoleChange(member)} disabled={Boolean(busyKey)}>
                        {member.role === 'co_admin' ? 'Demote' : 'Make admin'}
                      </button>
                    )}
                    <button className="danger-button small-action" type="button" onClick={() => void handleRemove(member)} disabled={Boolean(busyKey)}>Remove</button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>

      {canManage && (
        <div className="people-section">
          <div className="people-section-heading"><h3>Pending invites</h3></div>
          <div className="people-list">
            {pendingInvites.length === 0 ? (
              <div className="people-empty">No pending invites.</div>
            ) : pendingInvites.map((invite) => (
              <article className="person-row" key={invite.id}>
                <div className="person-avatar">{(invite.full_name || invite.email || invite.phone || 'I').charAt(0).toUpperCase()}</div>
                <div className="person-copy">
                  <strong>{invite.full_name || invite.email || invite.phone || 'Share invite'}</strong>
                  <span>
                    {invite.email || invite.phone || 'Share / copy'} · {invite.role_to_assign === 'co_admin' ? 'Co-admin' : 'Guest'} · {formatInviteCode(invite.invite_code ?? '')}
                  </span>
                </div>
                <span className="people-status-pill">{invite.status || 'invited'}</span>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
